import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const serviceData = [
  {
    icon: 'ri-global-line',
    titleKey: 'services.web.title',
    descKey: 'services.web.desc',
    featureKeys: ['services.web.f1', 'services.web.f2', 'services.web.f3', 'services.web.f4'],
    color: 'from-amber-50 to-orange-50',
    accent: 'text-amber-600',
    border: 'border-amber-200',
  },
  {
    icon: 'ri-smartphone-line',
    titleKey: 'services.app.title',
    descKey: 'services.app.desc',
    featureKeys: ['services.app.f1', 'services.app.f2', 'services.app.f3', 'services.app.f4'],
    color: 'from-teal-50 to-cyan-50',
    accent: 'text-teal-600',
    border: 'border-teal-200',
  },
  {
    icon: 'ri-lightbulb-line',
    titleKey: 'services.consult.title',
    descKey: 'services.consult.desc',
    featureKeys: ['services.consult.f1', 'services.consult.f2', 'services.consult.f3', 'services.consult.f4'],
    color: 'from-rose-50 to-pink-50',
    accent: 'text-rose-600',
    border: 'border-rose-200',
  },
  {
    icon: 'ri-shield-check-line',
    titleKey: 'services.maintain.title',
    descKey: 'services.maintain.desc',
    featureKeys: ['services.maintain.f1', 'services.maintain.f2', 'services.maintain.f3', 'services.maintain.f4'],
    color: 'from-violet-50 to-indigo-50',
    accent: 'text-violet-600',
    border: 'border-violet-200',
  },
];

const statsKeys = [
  { numKey: 'services.stat1.num', labelKey: 'services.stat1.label' },
  { numKey: 'services.stat2.num', labelKey: 'services.stat2.label' },
  { numKey: 'services.stat3.num', labelKey: 'services.stat3.label' },
  { numKey: 'services.stat4.num', labelKey: 'services.stat4.label' },
];

function ServiceCard({ service, index }: { service: typeof serviceData[0]; index: number }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group bg-gradient-to-br ${service.color} rounded-2xl p-7 border ${service.border} transition-all duration-700 hover:-translate-y-1 cursor-default ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-white mb-5 ${service.accent}`}>
        <i className={`${service.icon} text-2xl`}></i>
      </div>
      <h3 className="text-[#1e3a4a] text-xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
        {t(service.titleKey)}
      </h3>
      <p className="text-[#555] text-sm leading-relaxed mb-5">{t(service.descKey)}</p>
      <ul className="flex flex-wrap gap-2">
        {service.featureKeys.map((fk) => (
          <li key={fk} className={`text-xs px-3 py-1 rounded-full bg-white/70 ${service.accent} font-medium border ${service.border}`}>
            {t(fk)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Services() {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-24 md:py-32 bg-[#FFF9F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 border border-amber-200 mb-5">
            <i className="ri-service-line text-amber-600 text-sm"></i>
            <span className="text-amber-700 text-xs font-semibold tracking-wider uppercase">{t('services.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1e3a4a] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('services.title')}
          </h2>
          <p className="text-[#666] text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {t('services.desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {serviceData.map((service, i) => (
            <ServiceCard key={service.titleKey} service={service} index={i} />
          ))}
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 py-12 border-t border-amber-100">
          {statsKeys.map((stat) => (
            <div key={stat.labelKey} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-amber-500 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t(stat.numKey)}
              </div>
              <div className="text-[#666] text-sm font-medium">{t(stat.labelKey)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
